// src/hooks/useArchivos.js
import { useState, useCallback, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { addDoc, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { orgSubcollection } from "../utils/firePaths";

/**
 * Hook versátil para subir/leer/borrar archivos en el modelo ORG.
 * Firestore: /orgs/{orgId}/{ruta}/{parentId}/archivos
 * Storage : /orgs/{orgId}/{ruta}/{parentId}/archivos/{file.name}
 *
 * @param {string} orgId      ID del tenant (org)
 * @param {string} ruta       Colección padre dentro del org (ej: "cotizaciones", "proyectos", etc.)
 * @param {string} parentId   ID del documento dentro de `ruta`
 */
export default function useArchivos(orgId, ruta, parentId) {
  const [archivos, setArchivos] = useState([]);
  const [cargando, setCargando] = useState(false);

  const canUse = Boolean(orgId && ruta && parentId);

  // colección: orgs/{orgId}/{ruta}/{parentId}/archivos
  const archivosCol = useMemo(() => {
    if (!canUse) return null;
    return orgSubcollection(orgId, ruta, parentId, "archivos");
  }, [canUse, orgId, ruta, parentId]);

  // 1) Suscripción en tiempo real
  useEffect(() => {
    if (!archivosCol) {
      setArchivos([]);
      return;
    }

    const unsub = onSnapshot(
      archivosCol,
      (snap) => {
        setArchivos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error("Error suscribiéndose a archivos:", err),
    );

    return () => unsub();
  }, [archivosCol]);

  // 2) Subir archivos
  const manejarArchivos = useCallback(
    async (e) => {
      if (!canUse) return;

      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      // evita duplicados por nombre
      const sinDupli = files.filter(
        (f) =>
          !archivos.some(
            (a) => (a.nombre || "").toLowerCase() === f.name.toLowerCase(),
          ),
      );

      if (!sinDupli.length) {
        Swal.fire({ icon: "info", title: "Archivos duplicados" });
        e.target.value = "";
        return;
      }

      if (archivos.length + sinDupli.length > 10) {
        Swal.fire({ icon: "error", title: "Límite: 10 archivos" });
        e.target.value = "";
        return;
      }

      setCargando(true);
      try {
        const storage = getStorage();

        for (const file of sinDupli) {
          // Storage path org-based
          const path = `orgs/${orgId}/${ruta}/${parentId}/archivos/${file.name}`;
          const refSto = storageRef(storage, path);

          await uploadBytes(refSto, file);
          const url = await getDownloadURL(refSto);

          // Metadata en Firestore
          await addDoc(archivosCol, {
            nombre: file.name,
            url,
            path,
            size: file.size,
            type: file.type || "",
            creadoEn: new Date(),
          });
        }
      } catch (err) {
        console.error(err);
        Swal.fire({ icon: "error", title: "Error al cargar archivos" });
      } finally {
        setCargando(false);
        // limpia input para que puedas volver a subir el mismo archivo si lo borras
        e.target.value = "";
      }
    },
    [canUse, archivos, archivosCol, orgId, ruta, parentId],
  );

  // 3) Eliminar
  const eliminarArchivo = useCallback(
    (id, path) => {
      if (!canUse) return;

      Swal.fire({
        title: "¿Eliminar archivo?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
      }).then(async (res) => {
        if (!res.isConfirmed) return;

        try {
          // Borra metadata en Firestore
          await deleteDoc(
            doc(db, "orgs", orgId, ruta, parentId, "archivos", id),
          );

          // Borra file en Storage (si path existe)
          if (path) {
            const storage = getStorage();
            await deleteObject(storageRef(storage, path));
          }

          Swal.fire("Eliminado", "El archivo fue eliminado.", "success");
        } catch (err) {
          console.error(err);
          Swal.fire("Error", "No se pudo eliminar.", "error");
        }
      });
    },
    [canUse, orgId, ruta, parentId],
  );

  // 4) Descargar
  const descargarArchivo = useCallback((url, nombre) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre || "archivo";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // 5) Iconos según extensión
  const obtenerIcono = (ext) => {
    const e = (ext || "").toLowerCase();
    if (["xls", "xlsx", "csv"].includes(e))
      return { icon: "bi-file-earmark-excel", color: "green" };
    if (["doc", "docx"].includes(e))
      return { icon: "bi-file-earmark-word", color: "blue" };
    if (e === "pdf") return { icon: "bi-file-earmark-pdf", color: "red" };
    if (["jpg", "jpeg", "png", "svg", "webp"].includes(e))
      return { icon: "bi-image", color: "blue" };
    return { icon: "bi-file-earmark", color: "gray" };
  };

  return {
    archivos,
    cargando,
    manejarArchivos,
    eliminarArchivo,
    descargarArchivo,
    obtenerIcono,
  };
}
